import type { ReportFilter } from './model';
import { ReportDimension, ReportMetric, ReportType } from './reportEnums';

interface ReportSelectionRestriction {
  whenMetrics?: readonly ReportMetric[];
  whenDimensions?: readonly ReportDimension[];
  /** Active only when every listed dimension is selected. */
  whenAllDimensions?: readonly ReportDimension[];
  allowedMetrics?: readonly ReportMetric[];
  allowedDimensions?: readonly ReportDimension[];
  excludedMetrics?: readonly ReportMetric[];
  excludedDimensions?: readonly ReportDimension[];
  excludedFilters?: readonly (keyof ReportFilter)[];
  source?: ReportType;
}

const intentIqMetrics = [
  ReportMetric.IiqResolvedRequests,
  ReportMetric.IiqAdImpressions,
  ReportMetric.IiqRevenue,
];

const omidDimensions = [ReportDimension.Omidpn, ReportDimension.Omidpv];
const demandDimensions = [
  ReportDimension.DemandPartner,
  ReportDimension.DemandPartnerOwner,
  ReportDimension.DemandIntegration,
  ReportDimension.BidderCode,
];

// Rules operate on user-selected measures; dimension display fields are not measures.
// Add another entry to describe future restricted metric/dimension combinations.
const reportSelectionRestrictions: readonly ReportSelectionRestriction[] = [
  { whenMetrics: [ReportMetric.Deal], excludedMetrics: [ReportMetric.AuctionType] },
  { whenDimensions: [ReportDimension.Deal], excludedDimensions: [ReportDimension.AuctionType] },
  {
    whenMetrics: intentIqMetrics,
    whenDimensions: [ReportDimension.IiqArm, ReportDimension.Browser],
    allowedMetrics: intentIqMetrics,
    allowedDimensions: [
      ReportDimension.Day,
      ReportDimension.Hour,
      ReportDimension.Network,
      ReportDimension.Publisher,
      ReportDimension.Placement,
      ReportDimension.App,
      ReportDimension.PublisherDomain,
      ReportDimension.IiqArm,
      ReportDimension.Browser,
      ReportDimension.DemandPartner,
      ReportDimension.DemandIntegration,
    ],
    source: ReportType.IntentIq,
  },
  {
    whenMetrics: [ReportMetric.IiqResolvedRequests],
    excludedDimensions: [ReportDimension.DemandPartner, ReportDimension.DemandIntegration],
    excludedFilters: [
      'dpIds',
      'demandPartnerIds',
      'demandIntegrationIds',
      'excludeDpIds',
      'excludeDemandPartnerIds',
      'excludeDemandIntegrationIds',
    ],
  },
  // SG-912: OM SDK partner fields exist only in the request/bid tables, not the master revenue ones.
  // MadSense scope dimensions and filters are already rejected by madSenseSelectionCompatible.
  {
    whenDimensions: omidDimensions,
    excludedDimensions: [
      ReportDimension.LineItem,
      ReportDimension.Order,
      ReportDimension.Advertiser,
      ReportDimension.Brand,
      ReportDimension.Agency,
      ReportDimension.Creative,
      ReportDimension.Deal,
      ReportDimension.LrSegment,
    ],
    excludedFilters: ['lineItemIds', 'orderIds', 'dealIds'],
  },
  ...omidDimensions.map((dimension) => ({
    whenAllDimensions: [dimension, ReportDimension.SchainNodeDomain],
    excludedDimensions: demandDimensions,
  })),
];

function activeRestrictions(
  dimensions: readonly ReportDimension[],
  metrics: readonly ReportMetric[],
) {
  const metricSet = new Set(metrics);
  const dimensionSet = new Set(dimensions);
  return reportSelectionRestrictions.filter(
    (rule) =>
      rule.whenMetrics?.some((metric) => metricSet.has(metric)) ||
      rule.whenDimensions?.some((dimension) => dimensionSet.has(dimension)) ||
      rule.whenAllDimensions?.every((dimension) => dimensionSet.has(dimension)),
  );
}

export function meetsReportSelectionRestrictions(
  dimensions: readonly ReportDimension[],
  metrics: readonly ReportMetric[],
  filters: Partial<ReportFilter> = {},
) {
  const metricSet = new Set(metrics);
  const dimensionSet = new Set(dimensions);
  return activeRestrictions(dimensions, metrics).every(
    (rule) =>
      (!rule.allowedMetrics || metrics.every((metric) => rule.allowedMetrics?.includes(metric))) &&
      (!rule.allowedDimensions ||
        dimensions.every((dimension) => rule.allowedDimensions?.includes(dimension))) &&
      !rule.excludedMetrics?.some((metric) => metricSet.has(metric)) &&
      !rule.excludedDimensions?.some((dimension) => dimensionSet.has(dimension)) &&
      !rule.excludedFilters?.some((key) => Array.isArray(filters[key]) && filters[key].length > 0),
  );
}

export function reportSourceForSelection(
  dimensions: readonly ReportDimension[],
  metrics: readonly ReportMetric[],
) {
  return (
    activeRestrictions(dimensions, metrics).find((rule) => rule.source)?.source ??
    ReportType.Exchange
  );
}
